/**
 * Turning a schedule definition into concrete dose occurrences.
 *
 * This is THE shared function. The mobile app calls it to arm OS-local alarms;
 * the API Worker calls it to write `dose_events` rows that the patient's
 * Durable Object escalates from. If the two ever disagree, a caregiver gets
 * woken at 3am for a dose that was never due. Hence: pure, deterministic,
 * property-tested.
 */

import {
  DAY_MS,
  HOUR_MS,
  MINUTE_MS,
  addDaysToLocalDate,
  daysBetweenLocalDates,
  formatLocalDate,
  formatLocalTime,
  localDateAt,
  localDateTimeToInstant,
  parseLocalTime,
  toWallClock,
  weekdayOfLocalDate,
} from './time.js';
import {
  DEFAULT_MEAL_TIMES,
  type DoseOccurrence,
  type LocalDate,
  type MaterializeOptions,
  type AnchorTimes,
  type ScheduleDef,
} from './types.js';

/**
 * Guard against pathological input (`intervalHours: 0.001`) producing an
 * unbounded loop on a phone. A legitimate schedule never comes near this.
 */
const MAX_OCCURRENCES = 5_000;

export class ScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScheduleError';
  }
}

/**
 * Expand one schedule into every dose falling in `[from, to)`.
 *
 * Guarantees, relied upon by callers and enforced by the property tests:
 *  - results are sorted ascending by `dueAt`
 *  - `dueAt` values are unique (matching the `UNIQUE(scheduleId, dueAt)`
 *    constraint that makes re-materialisation idempotent)
 *  - every `dueAt` lies within `[from, to)`
 *  - `windowStart <= dueAt < windowEnd`
 */
export function materialize(
  schedule: ScheduleDef,
  options: MaterializeOptions,
): DoseOccurrence[] {
  const { timeZone, from, to } = options;
  const mealTimes = options.mealTimes ?? DEFAULT_MEAL_TIMES;

  if (to <= from) return [];
  if (schedule.type === 'prn') return []; // as-needed: never alarms

  validate(schedule);

  const raw =
    schedule.type === 'interval'
      ? intervalOccurrences(schedule, timeZone, from, to)
      : dailyOccurrences(schedule, timeZone, mealTimes, from, to, options.otherDays ?? null);

  const inRange = raw.filter((o) => o.dueAt >= from && o.dueAt < to);
  inRange.sort((a, b) => a.dueAt - b.dueAt);

  // Two anchors can legitimately resolve to the same instant (dinner+0 and
  // bedtime-120 when the patient eats at 20:00 and sleeps at 22:00). The DB
  // treats that as one dose, so we must too.
  const deduped: DoseOccurrence[] = [];
  for (const o of inRange) {
    if (deduped[deduped.length - 1]?.dueAt !== o.dueAt) deduped.push(o);
  }
  return deduped;
}

/** Expand several schedules at once, merged and sorted. Used by "what do I take now?". */
export function materializeMany(
  schedules: ScheduleDef[],
  options: MaterializeOptions,
): DoseOccurrence[] {
  return schedules
    .flatMap((s) => materialize(s, options))
    .sort((a, b) => a.dueAt - b.dueAt || a.scheduleId.localeCompare(b.scheduleId));
}

/**
 * The next dose at or after `after`. Walks forward in widening windows so that
 * a schedule with a long gap (a 21-on/7-off cycle) still resolves, but a truly
 * finished course terminates instead of scanning forever.
 */
export function nextOccurrence(
  schedule: ScheduleDef,
  timeZone: string,
  after: number,
  mealTimes?: AnchorTimes,
  maxLookaheadDays = 120,
): DoseOccurrence | null {
  let span = 7;
  let cursor = after;
  while (span <= maxLookaheadDays) {
    const found = materialize(schedule, {
      timeZone,
      ...(mealTimes ? { mealTimes } : {}),
      from: cursor,
      to: after + span * DAY_MS,
    });
    if (found.length > 0) return found[0]!;
    cursor = after + span * DAY_MS;
    span *= 2;
  }
  return null;
}

// ---------------------------------------------------------------------------

function validate(schedule: ScheduleDef): void {
  if (schedule.graceMinutes < 0) throw new ScheduleError('graceMinutes must be >= 0');
  if (schedule.earlyMinutes < 0) throw new ScheduleError('earlyMinutes must be >= 0');
  if (schedule.type === 'interval') {
    if (!(schedule.intervalHours > 0)) {
      throw new ScheduleError('intervalHours must be > 0');
    }
    if (schedule.intervalHours < 0.25) {
      throw new ScheduleError('intervalHours below 15 minutes is not a medication schedule');
    }
  }
  if (schedule.cycle) {
    const { onDays, offDays } = schedule.cycle;
    if (onDays < 1) throw new ScheduleError('cycle.onDays must be >= 1');
    if (offDays < 0) throw new ScheduleError('cycle.offDays must be >= 0');
  }
}

/** `fixed_times` and `meal_relative`: walk civil dates, emit each slot. */
function dailyOccurrences(
  schedule: Exclude<ScheduleDef, { type: 'interval' } | { type: 'prn' }>,
  timeZone: string,
  mealTimes: AnchorTimes,
  from: number,
  to: number,
  otherDays: MaterializeOptions['otherDays'] = null,
): DoseOccurrence[] {
  // Widen by a day on each side: a meal anchor with a large offset can push a
  // dose across midnight in either direction.
  const firstDate = localDateAt(from - DAY_MS, timeZone);
  const lastDate = localDateAt(to + DAY_MS, timeZone);

  const out: DoseOccurrence[] = [];
  for (let date = firstDate; date <= lastDate; date = addDaysToLocalDate(date, 1)) {
    if (!isActiveOn(schedule, date)) continue;

    if (schedule.type === 'fixed_times') {
      for (const time of schedule.times) {
        parseLocalTime(time); // reject malformed input loudly
        out.push(
          occurrence(schedule, localDateTimeToInstant(date, time, timeZone), timeZone, schedule.doseQuantity),
        );
      }
    } else {
      // The other days' meal times, on those days (docs/33 C7).
      const meals = otherDays && otherDays.days.includes(weekdayOfLocalDate(date))
        ? ({ ...mealTimes, ...Object.fromEntries(Object.entries(otherDays.times).filter(([, v]) => v)) } as AnchorTimes)
        : mealTimes;
      for (const anchor of schedule.anchors) {
        const mealTime = meals[anchor.meal];
        /*
         * A person's own time that has gone produces nothing: it is deleted
         * only after the running instructions tied to it were moved off it
         * (routine-times.ts), and the rows that still name it are history —
         * a course closed the day before. A meal with no time is corrupt
         * input, said loudly.
         */
        if (!mealTime && anchor.meal.startsWith('time:')) continue;
        if (!mealTime) throw new ScheduleError(`No meal time configured for "${anchor.meal}"`);
        const base = localDateTimeToInstant(date, mealTime, timeZone);
        const dueAt = base + anchor.offsetMinutes * MINUTE_MS;
        out.push({
          ...occurrence(schedule, dueAt, timeZone, anchor.doseQuantity ?? schedule.doseQuantity),
          meal: anchor.meal,
        });
      }
    }
    if (out.length > MAX_OCCURRENCES) {
      throw new ScheduleError(`Schedule ${schedule.id} produced more than ${MAX_OCCURRENCES} doses`);
    }
  }
  return out;
}

/**
 * `interval`: a promise about elapsed real time, not wall-clock time. "Every 6
 * hours" stays 6 real hours across a DST change — which is what an antibiotic
 * course actually means, and is deliberately different from `fixed_times`.
 */
function intervalOccurrences(
  schedule: Extract<ScheduleDef, { type: 'interval' }>,
  timeZone: string,
  from: number,
  to: number,
): DoseOccurrence[] {
  const anchor = localDateTimeToInstant(schedule.startDate, schedule.anchorTime, timeZone);
  const step = schedule.intervalHours * HOUR_MS;

  const firstIndex = Math.max(0, Math.ceil((from - anchor) / step));
  const out: DoseOccurrence[] = [];

  for (let i = firstIndex; ; i++) {
    const dueAt = anchor + i * step;
    if (dueAt >= to) break;
    if (out.length > MAX_OCCURRENCES) {
      throw new ScheduleError(`Schedule ${schedule.id} produced more than ${MAX_OCCURRENCES} doses`);
    }
    // Weekday / cycle / endDate gates apply to the local date the dose lands on.
    if (isActiveOn(schedule, localDateAt(dueAt, timeZone))) {
      out.push(occurrence(schedule, dueAt, timeZone, schedule.doseQuantity));
    }
    // Past the end of the course there is nothing further to find.
    if (schedule.endDate && localDateAt(dueAt, timeZone) > schedule.endDate) break;
  }
  return out;
}

function occurrence(
  schedule: ScheduleDef,
  dueAt: number,
  timeZone: string,
  doseQuantity: number,
): DoseOccurrence {
  const wc = toWallClock(dueAt, timeZone);
  return {
    scheduleId: schedule.id,
    dueAt,
    windowStart: dueAt - schedule.earlyMinutes * MINUTE_MS,
    windowEnd: dueAt + schedule.graceMinutes * MINUTE_MS,
    doseQuantity,
    doseUnit: schedule.doseUnit,
    foodRelation: schedule.foodRelation,
    localDate: formatLocalDate(wc),
    localTime: formatLocalTime(wc),
  };
}

/** Course bounds, weekday restriction and on/off cycle, evaluated on a civil date. */
export function isActiveOn(schedule: ScheduleDef, date: LocalDate): boolean {
  if (date < schedule.startDate) return false;
  if (schedule.endDate && date > schedule.endDate) return false;

  if (schedule.daysOfWeek && schedule.daysOfWeek.length > 0) {
    if (!schedule.daysOfWeek.includes(weekdayOfLocalDate(date))) return false;
  }

  const cycle = schedule.cycle;
  if (cycle) {
    const period = cycle.onDays + cycle.offDays;
    if (period <= 0) return true;
    const elapsed = daysBetweenLocalDates(cycle.startDate, date);
    if (elapsed < 0) return false;
    if (elapsed % period >= cycle.onDays) return false;
  }
  return true;
}

/**
 * The dates of a course "for N days", started now — defect 136.
 *
 * `endDate` is INCLUSIVE (see `isActiveOn`): a dose on the end date still
 * reminds. The phone and the organisation console both set it to "now + N
 * days", which is N + 1 days of reminders — a 7-day antibiotic course
 * reminded for eight. Reminding somebody to take a medicine for a day longer
 * than the doctor wrote is exactly what this product must never do.
 *
 * Both also took "today" in UTC, so between midnight and 05:30 in India a
 * course started yesterday. Both dates are the person's own civil dates.
 */
export function courseDates(
  durationDays: number | undefined,
  timeZone: string,
  now: number = Date.now(),
): { startDate: LocalDate; endDate: LocalDate | null } {
  const startDate = localDateAt(now, timeZone);
  if (durationDays === undefined || !Number.isInteger(durationDays) || durationDays < 1) {
    return { startDate, endDate: null };
  }
  return { startDate, endDate: addDaysToLocalDate(startDate, durationDays - 1) };
}
